import type { IsoTimestamp } from './common.js';
import type { BotBusinessHours } from './leadBot.js';

// BUSINESS PROFILE — the fitness lead bot's knowledge base (Run N, 2026-10-15).
//
// Owned by server/modules/businessProfile/. It is its own module because the
// SMS bot is the FIRST reader, not the only one: KyckSocial's AI, ads and web
// chat will read it later. The house pattern applies — the owning module hosts,
// other surfaces import.
//
// ⚠️ EVERY FREE-TEXT FIELD HERE IS CLIENT-ASSERTED, UNTRUSTED CONTENT as far as
// the prompt is concerned: typed into a form by a person, fenced as data and
// never as instructions (the BotKnowledgeBase / renderClubProfileBlock posture).
//
// ⚠️⚠️ PRICES ARE TYPED TEXT, NOT INTEGER CENTS. A membership price is what a
// club writes on their brief ("28.95", "$99 on open term - $0 on others"). It is
// shown to the public verbatim and checked by the price verifier as text. Do NOT
// add any profile or promotion field to MONEY_HISTORY_FIELDS (the account
// History tab divides those by 100, which would turn "$21.95" into a plausible
// wrong figure).
//
// ⚠️ STAFFED HOURS ARE STRUCTURED AND AUTHORITATIVE (one source of truth per
// fact). They drive "hand over now or next staffed period?" and, in Run O, the
// booking calendar's windows. Access hours are only `access24x7` plus text — the
// public checks OPENING hours on Google, but staffed hours are not on Google.

/** Which surface performed an edit. Recorded on every audited write. */
export type ProfileEditSurface = 'kyck' | 'portal';

/**
 * ⚠️⚠️ COPIED SETUP (club onboarding, 2026-11-04). A club's setup can be copied from another club ("copy everything, then edit").
 * Everything copied carries where it came from and is marked "needs review" until a PERSON saves that section or clicks
 * "This is correct for <club>". Go-live is blocked while anything needs review, and the price verifier does NOT accept a figure
 * from a section that still needs review (src/lib/fitnessBot/priceVerifier.ts), so Sam cannot quote a copied price to a lead.
 * Both fields are SERVER-MANAGED: a client write can never set or clear them.
 */
export interface CopiedFrom {
  accountId: string;
  accountName: string;
  at: IsoTimestamp;
}

export type ProfileReviewSection =
  | 'club'
  | 'hours'
  | 'team'
  | 'videos'
  | 'differentiators'
  | 'memberships'
  | 'fees'
  | 'addOns'
  | 'policies'
  | 'voice'
  | 'faqs'
  | 'objections'
  | 'discounting'
  | 'extraHardStops'
  | 'messages'
  | 'booking';

export const PROFILE_REVIEW_SECTIONS: ReadonlyArray<{ key: ProfileReviewSection; label: string }> = [
  { key: 'club', label: 'Club details' },
  { key: 'hours', label: 'Hours' },
  { key: 'team', label: 'Team' },
  { key: 'videos', label: 'Videos' },
  { key: 'differentiators', label: 'What makes the club different' },
  { key: 'memberships', label: 'Memberships' },
  { key: 'fees', label: 'Fees' },
  { key: 'addOns', label: 'Add-ons' },
  { key: 'policies', label: 'Policies' },
  { key: 'voice', label: 'Voice' },
  { key: 'faqs', label: 'Common questions' },
  { key: 'objections', label: 'Objections' },
  { key: 'discounting', label: 'Discounting' },
  { key: 'extraHardStops', label: 'Extra hand-over reasons' },
  { key: 'messages', label: 'Fixed messages' },
  { key: 'booking', label: 'Booking details' },
];
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  mobile: string;
  email: string;
  /** Receives handoff / booking / distress alerts. */
  receivesAlerts: boolean;
}

export interface MembershipRow {
  id: string;
  name: string;
  /** Typed text, e.g. "28.95". Checked by the price verifier. */
  price: string;
  frequency: string;
  minimumTerm: string;
  included: string;
}

export interface FeeRow {
  id: string;
  label: string;
  value: string;
}

export interface AddOnRow {
  id: string;
  service: string;
  price: string;
  whenToRaise: string;
}

export interface FaqRow {
  id: string;
  question: string;
  answer: string;
  /**
   * ⚠️ A FAQ marked handover is a HARD STOP, not an answer. The example brief
   * answers "How do I cancel?" with "HUMAN HANDOVER". When the lead's text
   * matches its question the model is not consulted.
   */
  handover: boolean;
}

export interface ObjectionRow {
  id: string;
  theySay: string;
  weReply: string;
}

export type EmojiPreference = 'yes' | 'no' | 'sparingly';
export type TeReoPreference = 'no' | 'occasionally' | 'yes';

export const TONE_OPTIONS = [
  'Casual and friendly',
  'Warm but professional',
  'Direct and efficient',
  'Motivational and high-energy',
] as const;

export interface ProfilePolicies {
  contract: string;
  cancellation: string;
  freeze: string;
  coolingOff: string;
  billing: string;
  reciprocalAccess: string;
  guests: string;
}

export const POLICY_FIELDS: ReadonlyArray<{ key: keyof ProfilePolicies; label: string }> = [
  { key: 'contract', label: 'Contract length and what happens at the end' },
  { key: 'cancellation', label: 'Cancellation policy (notice period, fees)' },
  { key: 'freeze', label: 'Freeze / suspension policy' },
  { key: 'coolingOff', label: 'Cooling-off period for new members' },
  { key: 'billing', label: 'Payment method and billing cycle' },
  { key: 'reciprocalAccess', label: 'Access to other clubs' },
  { key: 'guests', label: 'Bringing a guest' },
];

export interface ProfileVoice {
  tones: string[];
  emoji: EmojiPreference;
  localSlang: string;
  neverSay: string;
  alwaysSay: string;
  useFirstNames: boolean;
  teReo: TeReoPreference;
  /** One message in the club's own voice — the strongest tone signal there is. */
  sampleMessage: string;
}

/**
 * ⚠️ FIXED WORDINGS. These are used as DETERMINISTIC TEMPLATES — when a hard
 * stop fires, the reply is exactly this text and the model is never consulted.
 * Blank means "use the shipped default" (see defaults.ts), never "send nothing".
 */
export interface ProfileFixedMessages {
  handover: string;
  notSure: string;
  optOutConfirmation: string;
  distress: string;
  /** Append "Reply STOP to opt out" to the first outbound message (server-side). */
  includeStopFooter: boolean;
  /** Run O: the booking confirmation template. {{Day}} {{Time}} {{ClubName}} {{Address}} {{WhatToBring}} {{WhereToGo}} */
  bookingConfirmation: string;
}

export interface ProfileBookingInfo {
  /** Minutes. The example brief: a tour is 15 minutes. */
  tourMinutes: number;
  whatToBring: string;
  whereToGo: string;
}

export type ProfileVideoKind = 'club_overview' | 'personal_introduction';

export const PROFILE_VIDEO_KINDS: ReadonlyArray<{ value: ProfileVideoKind; label: string }> = [
  { value: 'club_overview', label: 'Club Overview' },
  { value: 'personal_introduction', label: 'Personal Introduction' },
];

export const MAX_PROFILE_VIDEOS = 10;

/** A club video Sam may send. ⚠️ The description says what is IN it; it is not a fact source. */
export interface ProfileVideo {
  id: string;
  kind: ProfileVideoKind;
  /** YouTube only, stored as entered. */
  url: string;
  description: string;
  /** A TeamMember id for a personal introduction; null when none, or when that member was deleted. */
  teamMemberId: string | null;
}

export interface BusinessProfile {
  /** Doc id IS the account id — one profile per account, structurally. */
  id: string;
  accountId: string;
  accountName: string | null;

  club: {
    /** The full club name as customers should see it. */
    displayName: string;
    address: string;
    parking: string;
    phone: string;
    email: string;
    joinUrl: string;
    reviewUrl: string;
    /** https://www.instagram.com/<handle>, or ''. Normalised on save; see src/lib/fitnessBot/links.ts. */
    instagramUrl: string;
    /** LEGACY free text, kept in stored docs and read only as an Instagram fallback. Never edited. */
    socials?: string;
  };
  hours: {
    access24x7: boolean;
    accessHoursText: string;
    /** ⚠️ Authoritative. Same shape as BotBusinessHours. */
    staffedHours: BotBusinessHours;
    publicHolidayNote: string;
    tourTimesGuidance: string;
  };
  team: TeamMember[];
  videos: ProfileVideo[];
  differentiators: {
    topReasons: string;
    typicalMember: string;
    competitors: string;
    facilities: string;
    doNotHave: string;
  };
  memberships: MembershipRow[];
  fees: FeeRow[];
  addOns: AddOnRow[];
  policies: ProfilePolicies;
  voice: ProfileVoice;
  faqs: FaqRow[];
  objections: ObjectionRow[];
  discounting: {
    canDiscount: boolean;
    pushBackReply: string;
  };
  /**
   * ⚠️ FREE TEXT CANNOT BE DETECTED DETERMINISTICALLY. These go into the prompt
   * as model-judged handoff reasons and the UI says so. The hard-coded
   * categories (cancellation, injury, legal …) are the deterministic ones.
   */
  extraHardStops: string;
  messages: ProfileFixedMessages;
  booking: ProfileBookingInfo;

  /** ⚠️ Server-managed: where a copied setup came from, and which sections a person still has to check. */
  copiedFrom?: CopiedFrom | null;
  needsReview?: ProfileReviewSection[];

  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
  updatedBy: string;
  updatedBySurface: ProfileEditSurface;
}

/** What an editor may send: everything except identity + stamps. */
export type BusinessProfileInput = Omit<
  BusinessProfile,
  | 'id'
  | 'accountId'
  | 'accountName'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'updatedBy'
  | 'updatedBySurface'
  | 'copiedFrom'
  | 'needsReview'
>;

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

export type PromotionKind = 'always_on' | 'campaign';

export interface Promotion {
  id: string;
  accountId: string;
  kind: PromotionKind;
  title: string;
  /** Said to the lead verbatim. */
  exactWording: string;
  whatTheySave: string;
  terms: string;
  /**
   * ⚠️ LOCAL CALENDAR DATES (YYYY-MM-DD), INCLUSIVE, in the profile's staffed-
   * hours timezone — never UTC instants. A campaign "ends 30 Sep" ends at the
   * end of the 30th at the CLUB, not at UTC midnight. Required for a campaign;
   * FORBIDDEN (null) for always-on.
   */
  startDate: string | null;
  endDate: string | null;
  enabled: boolean;
  /** ⚠️ Server-managed (copied setup): cleared only when a person saves or confirms it. */
  copiedFrom?: CopiedFrom | null;
  needsReview?: boolean;
  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
  updatedBy: string;
  updatedBySurface: ProfileEditSurface;
}

export type PromotionInput = Pick<
  Promotion,
  'kind' | 'title' | 'exactWording' | 'whatTheySave' | 'terms' | 'startDate' | 'endDate' | 'enabled'
>;

export type PromotionStatus = 'scheduled' | 'live' | 'expired' | 'disabled';

/** Wire shape: a promotion plus its computed status (as of the request). */
export interface PromotionRow extends Promotion {
  status: PromotionStatus;
}
